import { useState } from 'react';
import {
  Color, PAY_RATE_UNITS, Size, choiceLabelKey, payChangePath, payChangeProblems, payDecisionPath, payRejectionProblem, t,
  type PayChangeDraft, type PaySection, type StringKey,
} from '@bms2/shared';
import { Button, DatePicker, RadioGroup, TextInput, Textarea } from '../../components/controls';
import { FormFrame } from '../../components/forms/FormFrame';
import { useDocumentToday } from '../../shell/ViewerZone';
import { post } from '../records/api';
import styles from './HrPages.module.css';

/**
 * Change Pay (piece 34, *Compensation*; decision 3781): the new rate per hour
 * or per year, the hours a pay period, the day it starts and the reason. 🔒 It
 * opens on the line that the raise is entered in the payroll service first, and
 * the save writes a Pending row a second holder of the hire permission decides.
 */
export function ChangePayWindow({ apiBaseUrl = '', employmentId, start, onClose, onSaved }: {
  apiBaseUrl?: string;
  employmentId: string;
  start: PayChangeDraft;
  onClose: () => void;
  onSaved: () => void;
}) {
  const today = useDocumentToday();
  const [draft, setDraft] = useState<PayChangeDraft>({ ...start, effectiveFrom: start.effectiveFrom || today });
  const [typed, setTyped] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const change = (next: PayChangeDraft) => {
    setTyped(true);
    setDraft(next);
  };
  const problems = payChangeProblems(draft);
  const save = async () => {
    const answer = await post<{ compensationId: string }>(payChangePath(employmentId), draft, apiBaseUrl);
    if (!answer.ok) return setRefusal(answer.message);
    onSaved();
  };
  return (
    <FormFrame
      host="window"
      open
      title={t('hr.pay.title')}
      width="wide"
      dataForm="change-pay"
      typed={typed}
      onCancel={onClose}
      refusal={refusal}
      onDismissRefusal={() => setRefusal(null)}
      act={{ label: t('hr.pay.act'), onAct: () => save(), disabled: problems.length > 0 || !typed, disabledReason: problems[0] ?? '' }}
    >
      <div className={styles.fields} data-change-pay={employmentId}>
        <p className={styles.meta} data-change-pay-payroll="true">{t('hr.pay.payrollFirst')}</p>
        <div className={styles.controls}>
          <TextInput size={Size.Small} label={t('hr.hire.payRate')} value={draft.payRate} onChange={(payRate) => change({ ...draft, payRate })} required name="payChangeRate" />
          <RadioGroup name="payChangeUnit" legend={t('hr.hire.payRateUnit')} orientation="horizontal" value={draft.payRateUnit || null}
            options={PAY_RATE_UNITS.map((value) => ({ value, label: t(choiceLabelKey('hr.payUnit', value) as StringKey) }))} onChange={(value) => change({ ...draft, payRateUnit: value as PayChangeDraft['payRateUnit'] })} />
          <TextInput size={Size.Small} label={t('hr.hire.hoursPerPeriod')} value={draft.hoursPerPayPeriod} onChange={(hoursPerPayPeriod) => change({ ...draft, hoursPerPayPeriod })} name="payChangeHours" />
        </div>
        <DatePicker size={Size.Medium} label={t('hr.pay.effectiveFrom')} value={draft.effectiveFrom} onChange={(effectiveFrom) => change({ ...draft, effectiveFrom })} required name="payChangeStart" />
        <Textarea size={Size.Large} label={t('hr.pay.reason')} value={draft.changeReason} onChange={(changeReason) => change({ ...draft, changeReason })} required name="payChangeReason" />
      </div>
    </FormFrame>
  );
}

/** Reject Pay Change: the reason the row keeps, through the Form frame with its refusal inside. */
function RejectPayWindow({ apiBaseUrl, employmentId, compensationId, onClose, onSaved }: {
  apiBaseUrl: string;
  employmentId: string;
  compensationId: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [reason, setReason] = useState('');
  const [refusal, setRefusal] = useState<string | null>(null);
  const problem = payRejectionProblem(reason);
  const save = async () => {
    const answer = await post<{ approvalStatus: string }>(payDecisionPath(employmentId, compensationId, 'reject'), { reason }, apiBaseUrl);
    if (!answer.ok) return setRefusal(answer.message);
    onSaved();
  };
  return (
    <FormFrame
      host="window"
      open
      title={t('hr.pay.rejectTitle')}
      dataForm="reject-pay"
      typed={reason !== ''}
      onCancel={onClose}
      refusal={refusal}
      onDismissRefusal={() => setRefusal(null)}
      act={{ label: t('hr.pay.rejectAct'), onAct: () => save(), disabled: problem !== null, disabledReason: problem ?? '' }}
    >
      <div className={styles.fields} data-reject-pay={compensationId}>
        <p className={styles.meta}>{t('hr.pay.rejectLead')}</p>
        <Textarea size={Size.Large} label={t('hr.pay.rejectReason')} value={reason} onChange={setReason} required name="payRejectReason" />
      </div>
    </FormFrame>
  );
}

/**
 * The Pay section's controls (decision 3781), drawn for a holder of the hire
 * permission reading as HR: Change Pay on an open job with nothing waiting, and
 * the waiting change's Approve and Reject for a second holder. 🔨 The person who
 * entered the change and the person it pays read why they cannot decide it.
 */
export function PayControls({ apiBaseUrl, employmentId, pay, onChanged }: { apiBaseUrl: string; employmentId: string; pay: PaySection; onChanged: () => void }) {
  const [changing, setChanging] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const approve = async (compensationId: string) => {
    setBusy(true);
    const answer = await post<{ approvalStatus: string }>(payDecisionPath(employmentId, compensationId, 'approve'), {}, apiBaseUrl);
    setBusy(false);
    if (!answer.ok) return setNotice(answer.message);
    setNotice(null);
    onChanged();
  };
  const pending = pay.pending;
  return (
    <div className={styles.fields} data-employee-pay={employmentId}>
      {pending ? (
        <div data-pay-pending={pending.id}>
          <p className={styles.meta}>{t('hr.pay.waiting')}</p>
          {pending.mayDecide ? (
            <div className={styles.controls}>
              <span data-pay-approve="open"><Button size={Size.Small} color={Color.Navy} text={[t('hr.pay.approve')]} actions={[() => void approve(pending.id)]} disabled={busy} /></span>
              <span data-pay-reject="open"><Button size={Size.Small} color={Color.Inactive} text={[t('hr.pay.reject')]} actions={[() => setRejecting(true)]} disabled={busy} /></span>
            </div>
          ) : (
            <p className={styles.meta} data-pay-why-not="true">{pending.whyNot}</p>
          )}
        </div>
      ) : null}
      {pay.mayChange ? (
        <div className={styles.controls}>
          <span data-pay-change="open"><Button size={Size.Small} color={Color.Navy} text={[t('hr.pay.button')]} actions={[() => setChanging(true)]} /></span>
        </div>
      ) : null}
      {notice ? <p className={styles.meta} role="alert" data-pay-notice="error">{notice}</p> : null}
      {/* 🔨 Mounted only while open and keyed to the job, so a reopening reads the rate in force afresh. */}
      {changing ? (
        <ChangePayWindow
          key={`pay-${employmentId}`}
          apiBaseUrl={apiBaseUrl}
          employmentId={employmentId}
          start={pay.start}
          onClose={() => setChanging(false)}
          onSaved={() => {
            setChanging(false);
            onChanged();
          }}
        />
      ) : null}
      {rejecting && pending ? (
        <RejectPayWindow
          key={`reject-${pending.id}`}
          apiBaseUrl={apiBaseUrl}
          employmentId={employmentId}
          compensationId={pending.id}
          onClose={() => setRejecting(false)}
          onSaved={() => {
            setRejecting(false);
            onChanged();
          }}
        />
      ) : null}
    </div>
  );
}
